
import { supabase } from "@/lib/supabase";

export type ReactionType = 'heart' | 'thumbsUp' | 'smile' | 'surprised' | 'angry';

export const handleReaction = async (postId: string, userId: string | undefined, type: ReactionType, commentId?: string) => {
  if (!userId) return null;
  
  const table = commentId ? 'comment_interactions' : 'post_interactions';
  const idField = commentId ? 'comment_id' : 'post_id';
  const id = commentId || postId;
  
  try {
    // First get the post owner and the reactor's username
    const [postResult, profileResult] = await Promise.all([
      supabase
        .from('posts')
        .select('user_id')
        .eq('id', postId)
        .maybeSingle(),
      supabase
        .from('profiles')
        .select('username')
        .eq('id', userId)
        .maybeSingle()
    ]);

    if (!postResult.data) throw new Error('Post not found');
    if (!profileResult.data) throw new Error('Profile not found');

    const post = postResult.data;
    const profile = profileResult.data;

    // Don't notify if user is reacting to their own post
    const shouldNotify = post.user_id !== userId;

    // Get existing reaction first
    const { data: existingReaction } = await supabase
      .from(table)
      .select('interaction_type')
      .eq(idField, id)
      .eq('user_id', userId)
      .limit(1)
      .maybeSingle();

    // If clicking the same reaction type, remove it
    if (existingReaction?.interaction_type === type) {
      const { error: deleteError } = await supabase
        .from(table)
        .delete()
        .eq(idField, id)
        .eq('user_id', userId);

      if (deleteError) throw deleteError;
      return null;
    }
    
    // If there was a different reaction, update it
    if (existingReaction) {
      const { error: updateError } = await supabase
        .from(table)
        .update({ interaction_type: type })
        .eq(idField, id)
        .eq('user_id', userId);

      if (updateError) throw updateError;
      return type;
    }

    // Insert new reaction if no existing reaction
    const { error: insertError } = await supabase
      .from(table)
      .insert([{ 
        [idField]: id, 
        user_id: userId, 
        interaction_type: type 
      }]);

    if (insertError) throw insertError;

    // Create notification for new reactions only if it's not the user's own post
    if (shouldNotify) {
      const { error: notificationError } = await supabase
        .from('notifications')
        .insert([{
          user_id: post.user_id,
          actor_id: userId,
          type: 'reaction',
          content: `@${profile.username} reacted with ${type} to your ${commentId ? 'comment' : 'post'}`
        }]);

      if (notificationError) {
        console.error('Error creating notification:', notificationError);
      }
    }

    return type;
  } catch (error: any) {
    console.error('Reaction error:', error);
    throw error;
  }
};

export const handleBookmark = async (postId: string, userId: string | undefined) => {
  if (!userId) return;
  
  try {
    const { data: existingBookmark } = await supabase
      .from('bookmarks')
      .select()
      .eq('post_id', postId)
      .eq('user_id', userId)
      .maybeSingle();

    if (existingBookmark) {
      await supabase
        .from('bookmarks')
        .delete()
        .eq('post_id', postId)
        .eq('user_id', userId);
    } else {
      await supabase
        .from('bookmarks')
        .insert([{ post_id: postId, user_id: userId }]);
    }

    return !existingBookmark;
  } catch (error: any) {
    console.error('Bookmark error:', error);
    throw error;
  }
};

export const getReactionColor = (type: ReactionType) => {
  switch (type) {
    case 'heart':
      return 'text-red-500 hover:text-red-600';
    case 'thumbsUp':
      return 'text-blue-500 hover:text-blue-600';
    case 'smile':
      return 'text-yellow-500 hover:text-yellow-600';
    case 'surprised':
      return 'text-purple-500 hover:text-purple-600';
    case 'angry':
      return 'text-orange-500 hover:text-orange-600';
    default:
      return 'text-gray-400';
  }
};

export const getCurrentReaction = async (postId: string, userId: string | undefined, commentId?: string) => {
  if (!userId) return null;
  
  const table = commentId ? 'comment_interactions' : 'post_interactions';
  const idField = commentId ? 'comment_id' : 'post_id';
  const id = commentId || postId;

  try {
    const { data, error } = await supabase
      .from(table)
      .select('interaction_type')
      .eq(idField, id)
      .eq('user_id', userId)
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    return data?.interaction_type as ReactionType | null;
  } catch (error) {
    console.error('Error fetching current reaction:', error);
    return null;
  }
};

export const getReactionCounts = async (postId: string, commentId?: string) => {
  console.log('Fetching reaction counts for:', commentId ? 'comment' : 'post', commentId || postId);
  
  try {
    const table = commentId ? 'comment_interactions' : 'post_interactions';
    const idField = commentId ? 'comment_id' : 'post_id';
    const id = commentId || postId;

    // Get reactions from the appropriate table
    const { data: rawData, error: rawError } = await supabase
      .from(table)
      .select('interaction_type')
      .eq(idField, id);

    if (rawError) {
      console.error('Error fetching reactions:', rawError);
      return [];
    }

    console.log('Raw reaction data:', rawData);

    // Group and count reactions
    const counts = rawData.reduce((acc: Record<string, number>, item) => {
      const type = item.interaction_type;
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});

    console.log('Counted reactions:', counts);

    // Transform into expected format
    const formattedCounts = Object.entries(counts).map(([type, count]) => ({
      type: type as ReactionType,
      count: count
    }));

    console.log('Formatted reaction counts:', formattedCounts);
    return formattedCounts;

  } catch (error) {
    console.error('Error in getReactionCounts:', error);
    return [];
  }
};
